"use client";
import { CarouselButton } from "@/components/common";
import { useCarouselButtons } from "@repo/lib/hooks/common";
import { useDotButton } from "@repo/lib/hooks/common";
import { Link } from "@/i18n/routing";
import { DotButton } from "@repo/components";
import { getStrapiMedia, shimmer, toBase64 } from "@repo/lib/utils";
import cx from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";

const OPTIONS = { loop: true };

const Hero = ({ data }) => {
  const { BannerSlide } = data;
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = useCarouselButtons(emblaApi);

  const isCarouselRequired = BannerSlide.length > 1;
  // TODO: Add a desktop and mobile placeholder for the carousel if the media is not available of failed to load

  if (BannerSlide.length === 0) return null;
  return (
    <section className="relative m-auto w-full" dir="ltr">
      {/* Carousel Buttons */}
      {isCarouselRequired && (
        <>
          <div className="absolute left-[15%] top-1/2 z-10 hidden -translate-y-1/2 md:block">
            <CarouselButton
              direction="prev"
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              className="rounded-full bg-white/20 p-2 text-white shadow-md transition-colors hover:bg-white/40"
            />
          </div>
          <div className="absolute right-[15%] top-1/2 z-10 hidden -translate-y-1/2 md:block">
            <CarouselButton
              direction="next"
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              className="rounded-full bg-white/20 p-2 text-white shadow-md transition-colors hover:bg-white/40"
            />
          </div>
        </>
      )}

      {/* Carousel */}
      <div
        className="overflow-hidden"
        ref={isCarouselRequired ? emblaRef : null}
      >
        <div className="flex">
          {BannerSlide?.map((slide) => (
            <div className="relative w-full flex-none" key={slide.id}>
              {
                //TODO: Remove the below div once banners are finalized
                /*
                <div className="absolute inset-0 bg-black/40 z-[1]"></div>
                */
              }
              <Link
                href={slide?.link?.url || "/"}
                aria-label={
                  slide?.title || slide?.desktopMedia?.file?.alternativeText
                }
              >
                {slide?.mobileMedia?.file?.url && (
                  <Image
                    src={getStrapiMedia(slide?.mobileMedia?.file?.url)}
                    alt={slide?.mobileMedia?.file?.alternativeText || ""}
                    width={slide?.mobileMedia?.file?.width}
                    height={slide?.mobileMedia?.file?.height}
                    className="mx-auto h-auto w-full object-cover md:hidden"
                    placeholder={`data:image/svg+xml;base64,${toBase64(
                      shimmer(
                        slide?.mobileMedia?.file?.width,
                        slide?.mobileMedia?.file?.height
                      )
                    )}`}
                    priority={slide.id === BannerSlide[0].id}
                    fetchPriority={
                      slide.id === BannerSlide[0].id ? "high" : "auto"
                    }
                    decoding="async"
                    loading={slide.id === BannerSlide[0].id ? "eager" : "lazy"}
                  />
                )}
                {slide?.desktopMedia?.file?.url && (
                  <Image
                    src={getStrapiMedia(slide?.desktopMedia?.file?.url)}
                    alt={slide?.desktopMedia?.file?.alternativeText || ""}
                    width={slide?.desktopMedia?.file?.width}
                    height={slide?.desktopMedia?.file?.height}
                    className="mx-auto hidden md:block"
                    placeholder={`data:image/svg+xml;base64,${toBase64(
                      shimmer(
                        slide?.desktopMedia?.file?.width,
                        slide?.desktopMedia?.file?.height
                      )
                    )}`}
                    priority={slide.id === BannerSlide[0].id}
                    fetchPriority={
                      slide.id === BannerSlide[0].id ? "high" : "auto"
                    }
                    decoding="async"
                    loading={slide.id === BannerSlide[0].id ? "eager" : "lazy"}
                  />
                )}
              </Link>
              {slide?.title && slide?.link?.url && slide?.link?.text && (
                <div className="absolute inset-0 z-[2]">
                  <div className="flex h-full flex-col items-center justify-center">
                    {slide?.title && (
                      <h1 className="font-heading rtl:font-arabic mb-4 max-w-2xl text-center text-3xl font-semibold text-white md:text-6xl md:leading-[80px]">
                        {slide.title}
                      </h1>
                    )}
                    {slide?.link?.url && slide?.link?.text && (
                      <Link
                        className="flex w-full items-center justify-center overflow-hidden"
                        href={slide?.link?.url || "/"}
                      >
                        <button className="bg-lightYellow group relative inline-flex h-9 items-center justify-start overflow-hidden px-8 font-medium shadow transition-all hover:bg-[#F0B565] md:h-11">
                          <span className="absolute bottom-0 left-0 mb-9 ml-9 size-48 -translate-x-full translate-y-full scale-0 rounded-full bg-white transition-all duration-1000 ease-out group-hover:mb-32 group-hover:ml-0 group-hover:translate-x-0 group-hover:scale-150"></span>
                          <span className="relative w-full text-left text-white transition-colors duration-300 ease-in-out group-hover:text-black">
                            {slide?.link?.text}
                          </span>
                        </button>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Carousel Dots */}
      {isCarouselRequired && (
        <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center space-x-2 rtl:space-x-reverse">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={cx("h-[8px] w-[20px]", {
                "bg-[#E5A855] rtl:mr-3": index === selectedIndex,
                "border-whote bg-[#868686] rtl:mr-3": index !== selectedIndex,
              })}
              aria-label={index}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;
